// SelectedSiteCard — the per-site summary next to the ProjectsMap.
//
// Structure (from the task spec):
//   Title (site_label)
//   Meta line (coords + area)
//   Score · tier · confidence
//   Best for / Watch out (derived from schema)
//   One headline row per group, using renderCell() so every value is
//   byte-identical to what appears in the compare table.
//   Full breakdown across N groups
//
// Opening the deep analysis panel is now driven by the map — clicking a
// pin navigates to the site-search view at #/?lat=X&lon=Y&project=Z
// (see ProjectsMap.jsx). The redundant "Open analysis →" button was
// removed 2026-09-02.

import React from 'react';
import {
  groupsFor, headlineFieldsFor, renderCell,
  confidence, bestFor, watchOut, groupStatus, capacityAge,
} from '../../config/displaySchema';
import { formatAreaHa, formatCoords, projectValue } from '../../utils/format';
import { tierForIndex, SeidrIndexExplainer } from '../SeidrIndex';
import GridFeasibilityBlock from './GridFeasibilityBlock';
import { useGridFeasibility } from './useGridFeasibility';
import OfftakeBlock from './OfftakeBlock';
import { ProvenanceIcon, ProvenanceSite } from '../ProvenancePopover';
import { FIELD_PROVENANCE } from '../../utils/fieldProvenance';
import { useOfftake } from './useOfftake';
import '../../styles/SelectedSiteCard.css';

/**
 * @param {{ project: object | null, mode: 'datacenter' | 'hydrogen' }} props
 */
// Screened sites qualified on a grid point from the grid companies'
// published capacity (WattApp); the block below is what Statnett's own lists
// say about the transmission station nearest the site. Say so, so the two
// never read as a contradiction. (Until 2026-10-13 this called the nearest
// station of any owner "the nearest Statnett substation".)
function screenGridNote(project) {
  const gp = projectValue(project, 'harbour_screen.grid_point');
  if (!gp || !gp.name) return null;
  const parts = [gp.available_mw != null ? `${Math.round(gp.available_mw)} MW free` : null,
                 gp.queue_mw != null ? `${Math.round(gp.queue_mw)} MW queued` : null,
                 gp.distance_km != null ? `${Number(gp.distance_km).toFixed(1)} km` : null].filter(Boolean).join(', ');
  const age = capacityAge(gp);
  const src = age.text.replace(/^WattApp/, 'as published in WattApp');
  return `The capacity test used ${gp.name}${gp.owner ? ` (station owner ${gp.owner})` : ''}${parts ? `: ${parts}` : ''}, ${src}.${age.stale ? ' Confirm the figures with the grid company before relying on them.' : ''} That queue is the grid company's. Below is Statnett's queue at the transmission station above it, a separate queue that a connection of 5 MW or more also joins.`;
}

export default function SelectedSiteCard({ project, mode }) {
  if (!project) {
    return (
      <div className="ssc-card ssc-empty">
        <div className="ssc-empty-msg">Select a site to see the summary.</div>
      </div>
    );
  }

  const score = project.readiness_score;
  const tier = score != null ? tierForIndex(score) : null;
  const conf = confidence(project);
  const bestForList = bestFor(project);
  const watchOutList = watchOut(project);

  const kommune = projectValue(project, '_parcel_props.kommune');
  const parcelId =
    projectValue(project, '_parcel_props.matrikkelnummer') ||
    projectValue(project, '_parcel_props.teigid') ||
    null;
  const areaHa = projectValue(project, '_parcel_props.areal_ha');

  // Meta line: coords + area. kommune·pid is available in _parcel_props
  // but for the current seed rows it duplicates site_label — omit here to
  // avoid the redundancy. Add back once users rename their projects.
  const metaBits = [];
  metaBits.push(formatCoords(project.lat, project.lon));
  if (areaHa != null) metaBits.push(formatAreaHa(areaHa));
  if (kommune && parcelId) metaBits.push(`${kommune} · ${parcelId}`);
  // Orgs with shared projects (branding.shared_projects) show colleagues'
  // sites too; say whose each one is.
  if (project.owner_email) metaBits.push(`saved by ${project.owner_email}`);

  const groupsWithRows = groupsFor(mode)
    .map(g => ({ group: g, fields: headlineFieldsFor(g.id, project, mode) }))
    .filter(g => g.fields.length > 0)
    // The OfftakeBlock below IS the port block for hydrogen / ammonia;
    // a second "Port & offtake" group with one row read as a contradiction.
    .filter(g => !((mode === 'hydrogen' || mode === 'ammonia') && g.group.id === 'port'));

  // Grid-outlook fetch: lives here (not the compare table) because the
  // panel is a per-selection view. Cache is inside the hook.
  const grid = useGridFeasibility(project);
  // Port & offtake (offtake Phase 1) — hydrogen/ammonia projects only;
  // a data-centre doesn't ship product by sea. Pass null so the hook
  // stays idle (and fetches nothing) in datacenter mode.
  const isAmmoniaLike = mode === 'hydrogen' || mode === 'ammonia';
  // ProjectsV2 may already have attached the payload as enrichment._offtake
  // (useOfftakeMany, for the compare table); reuse it and skip the fetch.
  const preloaded = project?.enrichment?._offtake;
  const offtakeHook = useOfftake(isAmmoniaLike && !preloaded ? project : null);
  const offtake = preloaded
    ? { status: 'ready', data: preloaded, error: null, retry: () => {} }
    : offtakeHook;

  // Every ⓘ on the card links to this site's records (utils/sourceLinks).
  const provenanceSite = { ...(project.enrichment || {}), lat: project.lat, lon: project.lon };

  return (
    <ProvenanceSite.Provider value={provenanceSite}>
    <div className="ssc-card">
      <header className="ssc-head">
        <div className="ssc-title">{project.site_label || 'Untitled site'}</div>
        <div className="ssc-meta">{metaBits.join(' · ')}</div>

        <div className="ssc-verdict">
          {score != null ? (
            <>
              <span className="ssc-score" style={{ color: tier.color }}>{Math.round(score)}</span>
              <span className="ssc-slash">/100</span>
              <span className="ssc-tier" style={{ color: tier.color }}>{tier.tier}</span>
              <span className="ssc-explain"><SeidrIndexExplainer inline /></span>
            </>
          ) : (
            <span className="ssc-tier ssc-na">Score pending</span>
          )}
        </div>

        <div className="ssc-confidence">{conf.text}</div>

        {bestForList.length > 0 && (
          <div className="ssc-derived-line">
            <span className="ssc-derived-label">Best for</span>
            <span className="ssc-derived-value">{bestForList.join(' · ')}</span>
          </div>
        )}
        {watchOutList.length > 0 && (
          <div className="ssc-derived-line">
            <span className="ssc-derived-label">Watch out</span>
            <span className="ssc-derived-value">{watchOutList.join(' · ')}</span>
          </div>
        )}
      </header>

      <div className="ssc-groups">
        <GridFeasibilityBlock
          status={grid.status}
          feasibility={grid.feasibility}
          statnett={grid.statnett}
          error={grid.error}
          retry={grid.retry}
          note={screenGridNote(project)}
          constraint={projectValue(project, 'tso_constraint')}
        />
        {isAmmoniaLike && (
          <OfftakeBlock
            status={offtake.status}
            data={offtake.data}
            error={offtake.error}
            retry={offtake.retry}
            project={project}
          />
        )}
        {groupsWithRows.map(({ group, fields }) => {
          // Never grade what hasn't been measured: if any headline row in
          // the group is "Not assessed", the pill reads "Partial" in grey
          // instead of a colour verdict.
          const anyNa = fields.some(f => renderCell(f, project).cls === 'na');
          const status = anyNa ? { cls: 'na', label: 'Partial', estimated: true } : groupStatus(group, project);
          return (
            <section key={group.id} className="ssc-group">
              <div className="ssc-group-head">
                <span className="ssc-group-label">{group.label}</span>
                {status && status.label && (
                  <span className={`ssc-status ssc-status-${status.cls}`}>{status.label}</span>
                )}
              </div>
              <div className="ssc-rows">
                {fields.map(f => {
                  const cell = renderCell(f, project);
                  const cls = cell.cls === 'warn' ? 'ssc-row-warn'
                            : cell.cls === 'block' ? 'ssc-row-block'
                            : cell.cls === 'na' ? 'ssc-row-na'
                            : '';
                  return (
                    <div key={f.key} className={`ssc-row ${cls}`}>
                      <div className="ssc-row-label">
                        {f.label}
                        {FIELD_PROVENANCE[f.key] && <ProvenanceIcon field={f.key} label={f.label} country={project.country || 'NO'} />}
                      </div>
                      <div className="ssc-row-value">
                        <div className="ssc-row-text">{cell.text}</div>
                        {cell.meta && <div className="ssc-row-meta">{cell.meta}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <footer className="ssc-foot">
        <span className="ssc-foot-note">Full breakdown across {groupsWithRows.length} groups · click the pin on the map to open analysis</span>
      </footer>
    </div>
    </ProvenanceSite.Provider>
  );
}
